import type * as Fabric from '@postermywall/fabricjs-2';
import {Gradient} from '@postermywall/fabricjs-2';
import type {RGBA} from '@Utils/color.util';
import {convertFabricColorToFillObject, getRgbaArrayFromFabricColor, hexToRgba, rgbaToRGBAString} from '@Utils/color.util';
import {FillTypes} from '@PosterWhiteboard/classes/fill.types';
import {DEFAULT_BACKGROUND_COLOR, DEFAULT_BACKGROUND_DURATION, DEFAULT_SECONDARY_BACKGROUND_COLOR, FULL_PAGE_OFFSET, FULL_PAGE_PADDING} from '@PosterWhiteboard/page/page.types';
import {PageBackgroundDropDownItem} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-background-option/page-background-option.types';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {BaseItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {RectangleItemObject} from '@PosterWhiteboard/items/rectangle-item/rectangle-item';
import type {DeepPartial} from '@/global';
import {v4 as uuidv4} from 'uuid';

export const getColorFillTypeForBackgroundDropdownItem = (backgroundDropDownItem: PageBackgroundDropDownItem): FillTypes => {
  switch (backgroundDropDownItem) {
    case PageBackgroundDropDownItem.SOLID:
      return FillTypes.SOLID;
    case PageBackgroundDropDownItem.GRADIENT:
    case PageBackgroundDropDownItem.LINEAR_GRADIENT:
      return FillTypes.LINEAR_GRADIENT;
    case PageBackgroundDropDownItem.RADIAL_GRADIENT:
      return FillTypes.RADIAL_GRADIENT;
    case PageBackgroundDropDownItem.TRANSPARENT:
      return FillTypes.NONE;
    default:
      throw new Error(`backgroundDropDownItem is not a color background type`);
  }
};

/**
 * Fill type to build when a colour is applied to a background. A transparent (NONE) background
 * has no colour to edit, so applying a colour turns it solid; every other type keeps its own.
 */
export const getFillTypeForAppliedColor = (currentFillType: FillTypes): FillTypes => {
  return currentFillType === FillTypes.NONE ? FillTypes.SOLID : currentFillType;
};

export const getBackgroundDropdownItemForColorFillType = (type: FillTypes): PageBackgroundDropDownItem => {
  switch (type) {
    case FillTypes.SOLID:
      return PageBackgroundDropDownItem.SOLID;
    case FillTypes.LINEAR_GRADIENT:
    case FillTypes.RADIAL_GRADIENT:
      return PageBackgroundDropDownItem.GRADIENT;
    case FillTypes.NONE:
      return PageBackgroundDropDownItem.TRANSPARENT;
    default:
      throw new Error(`backgroundDropDownItem is not a color background type`);
  }
};

/**
 * Adapts an existing color set to a target fill type — keeps colors when widening
 * (solid → gradient gains a derived secondary), trims when narrowing.
 */
export const getColorsForFillType = (newType: FillTypes, existingColors: RGBA[]): RGBA[] => {
  if (newType === FillTypes.NONE) {
    return existingColors;
  }
  if (newType === FillTypes.LINEAR_GRADIENT || newType === FillTypes.RADIAL_GRADIENT) {
    if (existingColors.length >= 2) {
      return existingColors;
    }
    const primary = existingColors[0] ?? hexToRgba(DEFAULT_BACKGROUND_COLOR);
    return [primary, hexToRgba(DEFAULT_SECONDARY_BACKGROUND_COLOR)];
  }
  return [existingColors[0] ?? hexToRgba(DEFAULT_BACKGROUND_COLOR)];
};

export const getFillColorForBackgroundDropdownItem = (backgroundDropDownItem: PageBackgroundDropDownItem): RGBA[] => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const activeBg = currentPage?.getActiveBackground();
  const primaryDefaultColor = hexToRgba(DEFAULT_BACKGROUND_COLOR);
  const secondaryDefaultColor = hexToRgba(DEFAULT_SECONDARY_BACKGROUND_COLOR);

  if (activeBg?.gitype === ITEM_TYPE.RECTANGLE) {
    const existing = getRgbaArrayFromFabricColor(activeBg.getFillObject());
    return getColorsForFillType(getColorFillTypeForBackgroundDropdownItem(backgroundDropDownItem), existing);
  }

  if (backgroundDropDownItem === PageBackgroundDropDownItem.SOLID || backgroundDropDownItem === PageBackgroundDropDownItem.TRANSPARENT) {
    return [primaryDefaultColor];
  }
  if (
    backgroundDropDownItem === PageBackgroundDropDownItem.GRADIENT ||
    backgroundDropDownItem === PageBackgroundDropDownItem.LINEAR_GRADIENT ||
    backgroundDropDownItem === PageBackgroundDropDownItem.RADIAL_GRADIENT
  ) {
    return [primaryDefaultColor, secondaryDefaultColor];
  }

  throw new Error('cannot get color for background type image');
};

const getLinearGradientOptsForBackground = (fillHeight: number, gradientFillColors: RGBA[]): Fabric.GradientOptions<'linear'> => {
  const x = 0;
  const y = fillHeight;

  const colorStops = [];
  for (let i = 0; i < gradientFillColors.length; i++) {
    colorStops.push({offset: (i + 1) / gradientFillColors.length, color: rgbaToRGBAString(gradientFillColors[i])});
  }

  return {
    colorStops,
    coords: {
      x1: x,
      x2: x,
      y1: -y,
      y2: y,
    },
    type: 'linear',
  };
};

const getRadialGradientOptsForBackground = (fillWidth: number, fillHeight: number, gradientFillColors: RGBA[]): Fabric.GradientOptions<'radial'> => {
  const centerX = fillWidth / 2;
  const centerY = fillHeight / 2;

  return {
    colorStops: [
      {offset: 0, color: rgbaToRGBAString(gradientFillColors[0])},
      {offset: 1, color: rgbaToRGBAString(gradientFillColors[1])},
    ],
    type: 'radial',
    coords: {
      x1: centerX,
      y1: centerY,
      r1: 0,
      x2: centerX,
      y2: centerY,
      r2: Math.max(fillWidth, fillHeight),
    },
  };
};

/** Builds a fabric-shaped fill (string | null | gradient object) sized to a poster of the given dimensions. */
export const buildFabricFillForBackgroundDimensions = (posterWidth: number, posterHeight: number, fillType: FillTypes, fillColors: RGBA[]): BaseItemObject['fill'] => {
  if (fillType === FillTypes.NONE) {
    return null;
  }
  if (fillType === FillTypes.LINEAR_GRADIENT) {
    return new Gradient(getLinearGradientOptsForBackground(posterHeight + FULL_PAGE_PADDING, fillColors)).toObject() as BaseItemObject['fill'];
  }
  if (fillType === FillTypes.RADIAL_GRADIENT) {
    return new Gradient(
      getRadialGradientOptsForBackground(posterWidth + FULL_PAGE_PADDING, posterHeight + FULL_PAGE_PADDING, fillColors)
    ).toObject() as BaseItemObject['fill'];
  }
  return rgbaToRGBAString(fillColors[0]);
};

/** Builds a fabric-shaped fill (string | null | gradient object) for a bg-rect of the given page. */
export const buildFabricFillForBackground = (page: Page, fillType: FillTypes, fillColors: RGBA[]): BaseItemObject['fill'] => {
  return buildFabricFillForBackgroundDimensions(page.poster.width, page.poster.height, fillType, fillColors);
};

/**
 * Resizes a color background item object in place to fill a poster of the given new dimensions.
 * Gradient fills are tied to the rect's coords, so we rebuild them at the new size; solid and
 * transparent fills are size-independent and are left as-is.
 */
export const resizeColorBackgroundItemObject = (bgObject: RectangleItemObject, newPosterWidth: number, newPosterHeight: number): void => {
  bgObject.left = FULL_PAGE_OFFSET;
  bgObject.top = FULL_PAGE_OFFSET;
  bgObject.width = newPosterWidth + FULL_PAGE_PADDING;
  bgObject.height = newPosterHeight + FULL_PAGE_PADDING;
  bgObject.scaleX = 1;
  bgObject.scaleY = 1;

  const {fillType, fillColor} = convertFabricColorToFillObject(bgObject.fill);
  if (fillType === FillTypes.LINEAR_GRADIENT || fillType === FillTypes.RADIAL_GRADIENT) {
    bgObject.fill = buildFabricFillForBackgroundDimensions(newPosterWidth, newPosterHeight, fillType, fillColor);
  }
};

/** Constructs a partial RectangleItemObject for a color/gradient/transparent bg covering the page. */
export const buildColorBackgroundItemObject = (
  page: Page,
  fillType: FillTypes,
  fillColors: RGBA[],
  opts: {uid?: string; onPageStartTime?: number; onPageEndTime?: number} = {}
): DeepPartial<RectangleItemObject> => {
  return {
    gitype: ITEM_TYPE.RECTANGLE,
    isBackground: true,
    uid: opts.uid ?? uuidv4(),
    onPageStartTime: opts.onPageStartTime ?? 0,
    // When adding the first background to a page that has items but no bgs, page.getDuration()
    // returns the max item end time — sizing the bg here keeps items from being truncated.
    // When the page is fully empty, getDuration() is 0, so fall back to a sensible default.
    onPageEndTime: opts.onPageEndTime ?? Math.max(DEFAULT_BACKGROUND_DURATION, page.getDuration()),
    left: FULL_PAGE_OFFSET,
    top: FULL_PAGE_OFFSET,
    width: page.poster.width + FULL_PAGE_PADDING,
    height: page.poster.height + FULL_PAGE_PADDING,
    scaleX: 1,
    scaleY: 1,
    rx: 0,
    ry: 0,
    fill: buildFabricFillForBackground(page, fillType, fillColors),
  };
};
